import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { AlertTriangle, BarChart3, Calculator, CheckCircle, Clock, Download, Eye, FileText, Loader2 } from 'lucide-react';

export const DeclarationsTab = ({ selectedCompany, token, formatCurrency, employees }) => {
  const [itsDeclarationHistory, setItsDeclarationHistory] = useState([]);
  const [selectedITSPeriod, setSelectedITSPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  const [itsPreviewData, setItsPreviewData] = useState(null);
  const [isITSPreviewLoading, setIsITSPreviewLoading] = useState(false);
  const [isITSExcelGenerating, setIsITSExcelGenerating] = useState(false);
  const [itsValidationErrors, setItsValidationErrors] = useState([]);
  const [itsDeclarationStatus, setItsDeclarationStatus] = useState('draft'); // draft, generated, transmitted
  
  // État pour la sous-section des déclarations (ITS ou CNPS)
  const [declarationSection, setDeclarationSection] = useState('its'); // 'its' ou 'cnps'
  
  // États pour la section Déclaration CNPS
  const [selectedCNPSPeriod, setSelectedCNPSPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  const [cnpsPreviewData, setCnpsPreviewData] = useState(null);
  const [isCNPSPreviewLoading, setIsCNPSPreviewLoading] = useState(false);
  const [isCNPSExcelGenerating, setIsCNPSExcelGenerating] = useState(false);

  // Fonctions pour la section Déclaration ITS complète
  const fetchITSDeclarationHistory = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/its-declarations-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItsDeclarationHistory(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique ITS:', error);
    }
  };

  const previewITSDeclarationData = async () => {
    if (!selectedCompany) {
      toast.error("Aucune entreprise sélectionnée");
      return;
    }

    setIsITSPreviewLoading(true);
    setItsValidationErrors([]);
    setItsPreviewData(null);

    try {
      console.log('Prévisualisation ITS pour:', {
        companyId: selectedCompany.id,
        year: selectedITSPeriod.year,
        month: selectedITSPeriod.month
      });

      const response = await axios.get(
        `${API}/companies/${selectedCompany.id}/its-declaration-preview/${selectedITSPeriod.year}/${selectedITSPeriod.month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Données de prévisualisation reçues:', response.data);
      setItsPreviewData(response.data);
      
      // Validation des données
      const errors = [];
      if (!response.data.validation.has_ncc) {
        errors.push("Le N°CC de l'entreprise est manquant");
      }
      if (!response.data.validation.has_employees) {
        errors.push("Aucun employé actif trouvé pour cette entreprise");
      }
      if (!response.data.validation.has_payrolls) {
        errors.push(`Aucune donnée de paie trouvée pour ${selectedITSPeriod.month}/${selectedITSPeriod.year}`);
      }
      
      setItsValidationErrors(errors);
      
      if (errors.length === 0) {
        toast.success("Données prévisualisées avec succès");
      } else {
        toast.warning(`${errors.length} problème(s) détecté(s)`);
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'aperçu des données ITS:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      toast.error("Erreur lors du chargement de l'aperçu des données");
      setItsPreviewData(null);
    } finally {
      setIsITSPreviewLoading(false);
    }
  };

  const generateAdvancedITSDeclaration = async () => {
    if (!selectedCompany) {
      toast.error("Aucune entreprise sélectionnée");
      return;
    }

    if (itsValidationErrors.length > 0) {
      toast.error("Veuillez corriger les erreurs avant de générer la déclaration");
      return;
    }

    if (!token) {
      toast.error("Session expirée. Veuillez vous reconnecter.");
      return;
    }

    setIsITSExcelGenerating(true);
    toast.info("Génération du fichier Excel en cours... Cela peut prendre quelques secondes.");
    
    try {
      console.log('🔄 Début génération Excel ITS:', {
        company: selectedCompany.name,
        period: `${selectedITSPeriod.year}/${selectedITSPeriod.month}`,
        tokenPresent: !!token
      });

      // Télécharger via axios pour pouvoir détecter les erreurs serveur
      // (évite d'enregistrer une page d'erreur HTML sous un nom .xlsm illisible)
      const downloadUrl = `${API}/companies/${selectedCompany.id}/declaration-its/${selectedITSPeriod.year}/${selectedITSPeriod.month}`;
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        timeout: 120000,
        headers: { Authorization: `Bearer ${token}` }
      });

      const contentType = response.headers['content-type'] || '';
      if (!contentType.includes('ms-excel') && !contentType.includes('officedocument')) {
        throw new Error(`Réponse inattendue du serveur (${contentType})`);
      }

      const cleanCompanyName = selectedCompany.name.replace(/[^a-zA-Z0-9]/g, '_');
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Declaration_ITS_${cleanCompanyName}_${selectedITSPeriod.year}_${selectedITSPeriod.month.toString().padStart(2, '0')}.xlsm`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      console.log('✅ Téléchargement lancé via lien direct');

      // Ajouter à l'historique
      const newDeclaration = {
        id: Date.now(),
        year: selectedITSPeriod.year,
        month: selectedITSPeriod.month,
        company_name: selectedCompany.name,
        generated_at: new Date().toISOString(),
        status: 'generated',
        employee_count: itsPreviewData?.summary?.employee_count || 0,  // Utiliser le nombre d'employés avec bulletins
        filename: `Declaration_ITS_${cleanCompanyName}_${selectedITSPeriod.year}_${selectedITSPeriod.month.toString().padStart(2, '0')}.xlsm`
      };
      
      setItsDeclarationHistory(prev => [newDeclaration, ...prev]);
      
      // Attendre un peu avant de désactiver le loading
      setTimeout(() => {
        setIsITSExcelGenerating(false);
        toast.success("Le téléchargement a été lancé. Vérifiez vos téléchargements.");
      }, 2000);
      
    } catch (error) {
      console.error('Erreur lors de la génération de la déclaration ITS:', error);
      toast.error("Erreur serveur lors de la génération du fichier Excel. Aucun fichier corrompu n'a été téléchargé — réessayez.");
      setIsITSExcelGenerating(false);
    }
  };

  useEffect(() => {
    if (selectedCompany) fetchITSDeclarationHistory();
  }, [selectedCompany?.id]);

  return (
    <>
                {/* En-tête et navigation des sous-sections */}
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">Déclarations</h2>
                  <Badge variant="secondary" className="text-sm">
                    Entreprise: {selectedCompany?.name || 'Aucune sélectionnée'}
                  </Badge>
                </div>

                {/* Navigation des sous-sections */}
                <div className="flex space-x-2 border-b border-gray-200 pb-2">
                  <Button
                    variant={declarationSection === 'its' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDeclarationSection('its')}
                    className={declarationSection === 'its' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Déclaration ITS
                  </Button>
                  <Button
                    variant={declarationSection === 'cnps' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDeclarationSection('cnps')}
                    className={declarationSection === 'cnps' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Déclaration CNPS
                  </Button>
                </div>

                {/* Section Déclaration ITS */}
                {declarationSection === 'its' && (
                  <>
                    <div className="flex items-center space-x-2 mb-4">
                      <FileText className="w-5 h-5 text-orange-600" />
                      <h3 className="text-xl font-semibold text-gray-800">Déclaration ITS (État 301)</h3>
                    </div>

                {!selectedCompany ? (
                  <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3">
                        <AlertTriangle className="w-6 h-6 text-yellow-600" />
                        <div>
                          <h3 className="font-medium text-yellow-800">Entreprise requise</h3>
                          <p className="text-yellow-700">Veuillez sélectionner une entreprise pour gérer les déclarations ITS.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Colonne 1: Génération de déclaration */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Nouvelle déclaration */}
                      <Card className="bg-white">
                        <CardHeader>
                          <CardTitle className="flex items-center space-x-2">
                            <FileText className="w-5 h-5 text-orange-600" />
                            <span>Nouvelle Déclaration ITS</span>
                          </CardTitle>
                          <CardDescription>
                            Générer une déclaration mensuelle ITS (État 301) avec validation automatique
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Sélection de période */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="its_year">Année</Label>
                              <select
                                id="its_year"
                                value={selectedITSPeriod.year}
                                onChange={(e) => setSelectedITSPeriod({...selectedITSPeriod, year: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                              >
                                {[...Array(5)].map((_, i) => {
                                  const year = new Date().getFullYear() - 2 + i;
                                  return (
                                    <option key={year} value={year}>{year}</option>
                                  );
                                })}
                              </select>
                            </div>
                            <div>
                              <Label htmlFor="its_month">Mois</Label>
                              <select
                                id="its_month"
                                value={selectedITSPeriod.month}
                                onChange={(e) => setSelectedITSPeriod({...selectedITSPeriod, month: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                              >
                                {[
                                  {value: 1, label: 'Janvier'}, {value: 2, label: 'Février'}, {value: 3, label: 'Mars'},
                                  {value: 4, label: 'Avril'}, {value: 5, label: 'Mai'}, {value: 6, label: 'Juin'},
                                  {value: 7, label: 'Juillet'}, {value: 8, label: 'Août'}, {value: 9, label: 'Septembre'},
                                  {value: 10, label: 'Octobre'}, {value: 11, label: 'Novembre'}, {value: 12, label: 'Décembre'}
                                ].map(month => (
                                  <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Boutons d'action */}
                          <div className="flex space-x-3">
                            <Button 
                              onClick={previewITSDeclarationData}
                              disabled={isITSPreviewLoading}
                              variant="outline"
                              className="flex-1"
                            >
                              {isITSPreviewLoading ? 'Chargement...' : 'Prévisualiser les données'}
                            </Button>
                            <Button 
                              onClick={generateAdvancedITSDeclaration}
                              disabled={itsValidationErrors.length > 0 || !itsPreviewData || isITSExcelGenerating}
                              className="flex-1 bg-orange-600 hover:bg-orange-700"
                            >
                              {isITSExcelGenerating ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Génération en cours...
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-2" />
                                  Générer Excel
                                </>
                              )}
                            </Button>
                          </div>

                          {/* Erreurs de validation */}
                          {itsValidationErrors.length > 0 && (
                            <Card className="bg-red-50 border-red-200">
                              <CardContent className="p-4">
                                <h4 className="font-medium text-red-800 mb-2">Erreurs de validation :</h4>
                                <ul className="text-red-700 space-y-1">
                                  {itsValidationErrors.map((error, index) => (
                                    <li key={`validation-error-${index}-${error.substring(0, 20).replace(/\s/g, '')}`} className="flex items-center space-x-2">
                                      <AlertTriangle className="w-4 h-4" />
                                      <span>{error}</span>
                                    </li>
                                  ))}
                                </ul>
                              </CardContent>
                            </Card>
                          )}

                          {/* Aperçu des données */}
                          {itsPreviewData && (
                            <Card className="bg-green-50 border-green-200">
                              <CardContent className="p-4">
                                <h4 className="font-medium text-green-800 mb-3">
                                  Aperçu des données - {selectedITSPeriod.month.toString().padStart(2, '0')}/{selectedITSPeriod.year}
                                </h4>
                                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-green-700">Employés actifs:</span>
                                      <span className="font-medium text-green-800">{itsPreviewData.summary.employee_count}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-green-700">Bulletins de paie:</span>
                                      <span className="font-medium text-green-800">{itsPreviewData.summary.payroll_count}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-green-700">N°CC Entreprise:</span>
                                      <span className="font-medium text-green-800">{itsPreviewData.company.ncc_number || 'Non défini'}</span>
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-green-700">Masse salariale brute:</span>
                                      <span className="font-medium text-green-800">
                                        {itsPreviewData.summary.total_gross_salary.toLocaleString('fr-FR')} FCFA
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-green-700">Masse salariale nette:</span>
                                      <span className="font-medium text-green-800">
                                        {itsPreviewData.summary.total_net_salary.toLocaleString('fr-FR')} FCFA
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-green-700">Total ITS:</span>
                                      <span className="font-medium text-green-800">
                                        {itsPreviewData.summary.total_its.toLocaleString('fr-FR')} FCFA
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Status de validation */}
                                <div className="flex items-center justify-between pt-3 border-t border-green-300">
                                  <span className="text-green-700 font-medium">Statut de validation:</span>
                                  <div className="flex items-center space-x-2">
                                    {itsValidationErrors.length === 0 ? (
                                      <>
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                        <span className="font-medium text-green-800">Prêt pour génération</span>
                                      </>
                                    ) : (
                                      <>
                                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                                        <span className="font-medium text-orange-800">{itsValidationErrors.length} erreur(s)</span>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Liste des employés (aperçu) */}
                                {itsPreviewData.employees.length > 0 && (
                                  <div className="mt-4 pt-3 border-t border-green-300">
                                    <span className="text-green-700 font-medium text-sm">Employés concernés:</span>
                                    <div className="mt-2 max-h-24 overflow-y-auto">
                                      {itsPreviewData.employees.slice(0, 5).map((employee, index) => (
                                        <div key={employee.id} className="text-xs text-green-600 flex justify-between">
                                          <span>{employee.first_name} {employee.last_name}</span>
                                          <span>{employee.position}</span>
                                        </div>
                                      ))}
                                      {itsPreviewData.employees.length > 5 && (
                                        <div className="text-xs text-green-600 italic mt-1">
                                          ... et {itsPreviewData.employees.length - 5} autre(s)
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          )}
                        </CardContent>
                      </Card>

                      {/* Informations importantes */}
                      <Card className="bg-blue-50 border-blue-200">
                        <CardHeader>
                          <CardTitle className="text-blue-900">Informations importantes</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="text-blue-800 space-y-2 text-sm">
                            <li className="flex items-start space-x-2">
                              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600" />
                              <span>Le fichier généré est basé sur le modèle officiel ITS (État 301)</span>
                            </li>
                            <li className="flex items-start space-x-2">
                              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600" />
                              <span>Les données sont automatiquement pré-remplies depuis votre système de paie</span>
                            </li>
                            <li className="flex items-start space-x-2">
                              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600" />
                              <span>Validation automatique des données obligatoires avant génération</span>
                            </li>
                            <li className="flex items-start space-x-2">
                              <AlertTriangle className="w-4 h-4 mt-0.5 text-orange-600" />
                              <span>Vérifiez toujours les données avant transmission aux services fiscaux</span>
                            </li>
                          </ul>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Colonne 2: Historique et statuts */}
                    <div className="space-y-6">
                      {/* Historique des déclarations */}
                      <Card className="bg-white">
                        <CardHeader>
                          <CardTitle className="flex items-center space-x-2">
                            <Clock className="w-5 h-5 text-gray-600" />
                            <span>Historique</span>
                          </CardTitle>
                          <CardDescription>Dernières déclarations générées</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {itsDeclarationHistory.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                              <p>Aucune déclaration générée</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {itsDeclarationHistory.slice(0, 5).map((declaration) => (
                                <div key={declaration.id} className="border rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium text-sm">
                                      {declaration.month.toString().padStart(2, '0')}/{declaration.year}
                                    </span>
                                    <Badge variant={
                                      declaration.status === 'generated' ? 'default' : 
                                      declaration.status === 'transmitted' ? 'secondary' : 'outline'
                                    }>
                                      {declaration.status === 'generated' ? 'Généré' : 
                                       declaration.status === 'transmitted' ? 'Transmis' : 'Brouillon'}
                                    </Badge>
                                  </div>
                                  <div className="text-xs text-gray-500 space-y-1">
                                    <div>{declaration.employee_count} employés</div>
                                    <div>{new Date(declaration.generated_at).toLocaleDateString('fr-FR')}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Statistiques rapides */}
                      <Card className="bg-white">
                        <CardHeader>
                          <CardTitle className="flex items-center space-x-2">
                            <BarChart3 className="w-5 h-5 text-green-600" />
                            <span>Statistiques</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Cette année:</span>
                            <span className="font-medium">{itsDeclarationHistory.filter(d => d.year === new Date().getFullYear()).length} déclarations</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Ce mois:</span>
                            <span className="font-medium">
                              {itsDeclarationHistory.filter(d => 
                                d.year === new Date().getFullYear() && 
                                d.month === new Date().getMonth() + 1
                              ).length > 0 ? '✅ Généré' : '❌ En attente'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Employés actifs:</span>
                            <span className="font-medium">{employees.length}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
                  </>
                )}

                {/* Section Déclaration CNPS */}
                {declarationSection === 'cnps' && (
                  <>
                    <div className="flex items-center space-x-2 mb-4">
                      <Calculator className="w-5 h-5 text-blue-600" />
                      <h3 className="text-xl font-semibold text-gray-800">Déclaration CNPS</h3>
                    </div>

                    {!selectedCompany ? (
                      <Card className="bg-yellow-50 border-yellow-200">
                        <CardContent className="p-6">
                          <div className="flex items-center space-x-3">
                            <AlertTriangle className="w-6 h-6 text-yellow-600" />
                            <div>
                              <h3 className="font-medium text-yellow-800">Entreprise requise</h3>
                              <p className="text-yellow-700">Veuillez sélectionner une entreprise pour gérer les déclarations CNPS.</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Colonne 1: Génération de déclaration CNPS */}
                        <div className="lg:col-span-2 space-y-6">
                          {/* Nouvelle déclaration CNPS */}
                          <Card className="bg-white">
                            <CardHeader>
                              <CardTitle className="flex items-center space-x-2">
                                <Calculator className="w-5 h-5 text-blue-600" />
                                <span>Nouvelle Déclaration CNPS</span>
                              </CardTitle>
                              <CardDescription>
                                Générer une déclaration mensuelle des cotisations CNPS
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              {/* Sélection de période */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label htmlFor="cnps_year">Année</Label>
                                  <select
                                    id="cnps_year"
                                    value={selectedCNPSPeriod.year}
                                    onChange={(e) => setSelectedCNPSPeriod({...selectedCNPSPeriod, year: parseInt(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    {[...Array(5)].map((_, i) => {
                                      const year = new Date().getFullYear() - 2 + i;
                                      return (
                                        <option key={year} value={year}>{year}</option>
                                      );
                                    })}
                                  </select>
                                </div>
                                <div>
                                  <Label htmlFor="cnps_month">Mois</Label>
                                  <select
                                    id="cnps_month"
                                    value={selectedCNPSPeriod.month}
                                    onChange={(e) => setSelectedCNPSPeriod({...selectedCNPSPeriod, month: parseInt(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    {[
                                      {value: 1, label: 'Janvier'}, {value: 2, label: 'Février'}, {value: 3, label: 'Mars'},
                                      {value: 4, label: 'Avril'}, {value: 5, label: 'Mai'}, {value: 6, label: 'Juin'},
                                      {value: 7, label: 'Juillet'}, {value: 8, label: 'Août'}, {value: 9, label: 'Septembre'},
                                      {value: 10, label: 'Octobre'}, {value: 11, label: 'Novembre'}, {value: 12, label: 'Décembre'}
                                    ].map(month => (
                                      <option key={month.value} value={month.value}>{month.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* Boutons d'action */}
                              <div className="flex space-x-3">
                                <Button 
                                  onClick={async () => {
                                    if (!selectedCompany) return;
                                    setIsCNPSPreviewLoading(true);
                                    try {
                                      const response = await axios.get(
                                        `${API}/companies/${selectedCompany.id}/cnps-declaration-preview/${selectedCNPSPeriod.year}/${selectedCNPSPeriod.month}`,
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      setCnpsPreviewData(response.data);
                                      toast.success('Aperçu CNPS chargé avec succès');
                                    } catch (error) {
                                      console.error('Erreur aperçu CNPS:', error);
                                      toast.error(error.response?.data?.detail || 'Erreur lors du chargement de l\'aperçu');
                                    } finally {
                                      setIsCNPSPreviewLoading(false);
                                    }
                                  }}
                                  disabled={isCNPSPreviewLoading}
                                  variant="outline"
                                  className="flex-1"
                                >
                                  {isCNPSPreviewLoading ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Chargement...
                                    </>
                                  ) : (
                                    <>
                                      <Eye className="w-4 h-4 mr-2" />
                                      Aperçu des données
                                    </>
                                  )}
                                </Button>
                                <Button 
                                  onClick={async () => {
                                    if (!selectedCompany) return;
                                    setIsCNPSExcelGenerating(true);
                                    try {
                                      const downloadUrl = `${API}/companies/${selectedCompany.id}/declaration-cnps/${selectedCNPSPeriod.year}/${selectedCNPSPeriod.month}`;
                                      const response = await axios.get(downloadUrl, {
                                        responseType: 'blob',
                                        timeout: 120000,
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      const contentType = response.headers['content-type'] || '';
                                      if (!contentType.includes('ms-excel') && !contentType.includes('officedocument')) {
                                        throw new Error(`Réponse inattendue du serveur (${contentType})`);
                                      }
                                      const blobUrl = URL.createObjectURL(response.data);
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      const cleanCompanyName = selectedCompany.name.replace(/[^a-zA-Z0-9]/g, '_');
                                      link.download = `Declaration_CNPS_${cleanCompanyName}_${selectedCNPSPeriod.year}_${selectedCNPSPeriod.month.toString().padStart(2, '0')}.xlsx`;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      URL.revokeObjectURL(blobUrl);
                                      toast.success('Déclaration CNPS générée avec succès');
                                    } catch (error) {
                                      console.error('Erreur génération CNPS:', error);
                                      toast.error("Erreur serveur lors de la génération CNPS. Aucun fichier corrompu n'a été téléchargé — réessayez.");
                                    } finally {
                                      setIsCNPSExcelGenerating(false);
                                    }
                                  }}
                                  disabled={isCNPSExcelGenerating}
                                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                                >
                                  {isCNPSExcelGenerating ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Génération...
                                    </>
                                  ) : (
                                    <>
                                      <Download className="w-4 h-4 mr-2" />
                                      Générer Excel CNPS
                                    </>
                                  )}
                                </Button>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Aperçu des données CNPS */}
                          {cnpsPreviewData && (
                            <Card className="bg-white">
                              <CardHeader>
                                <CardTitle className="flex items-center space-x-2">
                                  <Eye className="w-5 h-5 text-blue-600" />
                                  <span>Aperçu de la Déclaration CNPS</span>
                                </CardTitle>
                                <CardDescription>
                                  Période: {selectedCNPSPeriod.month}/{selectedCNPSPeriod.year}
                                </CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                  <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-700">{cnpsPreviewData.total_employees || 0}</div>
                                    <div className="text-sm text-blue-600">Employés</div>
                                  </div>
                                  <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-lg font-bold text-green-700">{formatCurrency(cnpsPreviewData.total_brut || 0)}</div>
                                    <div className="text-sm text-green-600">Masse salariale brute</div>
                                  </div>
                                  <div className="bg-orange-50 p-4 rounded-lg">
                                    <div className="text-lg font-bold text-orange-700">{formatCurrency(cnpsPreviewData.total_cnps_employee || 0)}</div>
                                    <div className="text-sm text-orange-600">Part salariale</div>
                                  </div>
                                  <div className="bg-purple-50 p-4 rounded-lg">
                                    <div className="text-lg font-bold text-purple-700">{formatCurrency(cnpsPreviewData.total_cnps_employer || 0)}</div>
                                    <div className="text-sm text-purple-600">Part patronale</div>
                                  </div>
                                </div>

                                {/* Tableau des employés */}
                                {cnpsPreviewData.employees && cnpsPreviewData.employees.length > 0 && (
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° CNPS</th>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Salaire Brut</th>
                                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Part Salariale</th>
                                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Part Patronale</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {cnpsPreviewData.employees.slice(0, 10).map((emp, idx) => (
                                          <tr key={idx} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-900">{emp.numero_cnps || 'N/A'}</td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{emp.nom_complet}</td>
                                            <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(emp.salaire_brut || 0)}</td>
                                            <td className="px-4 py-3 text-sm text-right text-orange-600">{formatCurrency(emp.cnps_employee || 0)}</td>
                                            <td className="px-4 py-3 text-sm text-right text-purple-600">{formatCurrency(emp.cnps_employer || 0)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot className="bg-gray-100">
                                        <tr>
                                          <td colSpan="2" className="px-4 py-3 text-sm font-bold text-gray-900">TOTAL</td>
                                          <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{formatCurrency(cnpsPreviewData.total_brut || 0)}</td>
                                          <td className="px-4 py-3 text-sm text-right font-bold text-orange-700">{formatCurrency(cnpsPreviewData.total_cnps_employee || 0)}</td>
                                          <td className="px-4 py-3 text-sm text-right font-bold text-purple-700">{formatCurrency(cnpsPreviewData.total_cnps_employer || 0)}</td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                    {cnpsPreviewData.employees.length > 10 && (
                                      <p className="text-sm text-gray-500 mt-2 text-center">
                                        ... et {cnpsPreviewData.employees.length - 10} autres employés
                                      </p>
                                    )}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          )}
                        </div>

                        {/* Colonne 2: Informations CNPS */}
                        <div className="space-y-6">
                          {/* Taux CNPS */}
                          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                            <CardHeader>
                              <CardTitle className="flex items-center space-x-2">
                                <Calculator className="w-5 h-5 text-blue-600" />
                                <span>Taux CNPS en vigueur</span>
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div className="space-y-2">
                                <h4 className="font-medium text-gray-800">Part Salariale</h4>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Retraite:</span>
                                  <span className="font-medium">6.3%</span>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <h4 className="font-medium text-gray-800">Part Patronale</h4>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Retraite:</span>
                                  <span className="font-medium">7.7%</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Prestations familiales:</span>
                                  <span className="font-medium">5.75%</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Accident du travail:</span>
                                  <span className="font-medium">2% - 5%</span>
                                </div>
                              </div>
                              <div className="border-t pt-3 mt-3">
                                <div className="flex justify-between text-sm font-bold">
                                  <span className="text-gray-800">Total employeur:</span>
                                  <span className="text-blue-700">15.45% - 18.45%</span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Statistiques rapides */}
                          <Card className="bg-white">
                            <CardHeader>
                              <CardTitle className="text-lg">Statistiques</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Employés actifs:</span>
                                <span className="font-medium">{employees.filter(e => e.is_active !== false).length}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Avec N° CNPS:</span>
                                <span className="font-medium">{employees.filter(e => e.numero_cnps).length}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Sans N° CNPS:</span>
                                <span className="font-medium text-red-600">{employees.filter(e => !e.numero_cnps).length}</span>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    )}
                  </>
                )}

    </>
  );
};
